import { createGetRequest } from 'app/selectors';
import { actionBinder } from 'utils/actionBinder';
import type { TActionCommonInterface } from 'utils/types';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { PayloadAction } from '@reduxjs/toolkit';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RequestStatus } from './requestSlice';
import { requestSlice } from './requestSlice';

export type TUseRequestUpdaterProps<Data = any, Variables = any> = TActionCommonInterface<Data> & {
    variables?: Variables;
};

export type useRequestParams<Data = any, Variables = any> = {
    requestId: string;
    query: string | TypedDocumentNode<Data, Variables>;
    variables?: Variables;
    withEffect?: boolean;
    dependenciesUpdater?: (depsVars: TUseRequestUpdaterProps<Data, Variables>) => void;
    onErrorUpdater?: (depsVars: TUseRequestUpdaterProps<Data, Variables>) => void;
};

type RequestDataReturnType<Data, Variables> = [
    Data,
    RequestStatus,
    any,
    (variables?: Variables, depsUpdater?: useRequestParams['dependenciesUpdater']) => PayloadAction,
    any,
    any,
];

export const useRequest = <Data, Variables = any>({
    requestId,
    query,
    variables,
    withEffect = true,
    dependenciesUpdater,
    onErrorUpdater,
}: useRequestParams<Data, Variables>): RequestDataReturnType<Data, Variables> => {
    const makeRequest = useMemo(() => {
        const boundMakeRequest = actionBinder(requestSlice.actions.makeRequest, requestId);

        // eslint-disable-next-line @typescript-eslint/no-shadow
        return (requestVariables?: Variables, requestDependenciesUpdater?: useRequestParams['dependenciesUpdater']) =>
            boundMakeRequest({
                query,
                variables: requestVariables,
                ...(dependenciesUpdater || requestDependenciesUpdater
                    ? { dependenciesUpdater: requestDependenciesUpdater || dependenciesUpdater }
                    : undefined),
                onErrorUpdater,
            });
    }, [dependenciesUpdater, onErrorUpdater, query, requestId]);

    const dispatch = useDispatch();
    const requestSelector = useMemo(() => createGetRequest(requestId), [requestId]);
    const { data, status, error } = useSelector(requestSelector);

    useEffect(() => {
        if (withEffect) {
            dispatch(makeRequest(variables));
        }
    }, [dispatch, makeRequest, variables, withEffect]);

    const setData = useMemo(() => actionBinder(requestSlice.actions.setData, requestId), [requestId]);

    const abortRequest = useMemo(() => actionBinder(requestSlice.actions.abortRequest, requestId), [requestId]);

    const parseErrors = useMemo(() => {
        if (typeof error === 'string') {
            return JSON.parse(error);
        }

        return error;
    }, [error]);

    return [data, status!, parseErrors, makeRequest, setData, abortRequest];
};
